import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AuthUser } from '@src/types';
import { getAdminMe, getMe } from '@src/services/auth.ts';
import { useAuthStore } from '@stores/authStore.ts';
import { useLocation } from 'react-router-dom';
import { refreshCsrfToken } from '@packages/axios/index.ts';

const PUBLIC_PATH_PREFIXES = [
  '/signin',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/admin/signin',
  '/admin/forgot-password',
  '/admin/reset-password',
];

function isPublicPath(pathname: string) {
  return PUBLIC_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

export const useGetMe = () => {
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const { auth } = useAuthStore();

  const me = useMemo(() => auth.user, [auth.user]);

  // De-dupe requests so multiple components don’t trigger multiple /me calls.
  const inflightRef = useRef<Promise<AuthUser | null> | null>(null);
  const hadAuthErrorRef = useRef(false);

  const fetchMe = useCallback(async () => {
    if (inflightRef.current) return inflightRef.current;

    const task = (async () => {
      setLoading(true);

      try {
        // CSRF token is stored in memory, so on hard reload we must re-hydrate it
        // before calling endpoints that require CSRF protection.
        await refreshCsrfToken().catch(() => {});

        const isAdminPath = location.pathname.startsWith('/admin');

        const res = isAdminPath ? await getAdminMe() : await getMe();

        hadAuthErrorRef.current = false;
        auth.setUser(res.data);
        localStorage.setItem(
          'permissions',
          JSON.stringify(res.data.permissions)
        );

        return res.data;
      } catch {
        // Mark that we failed to hydrate; avoid hammering /me in a render loop.
        hadAuthErrorRef.current = true;

        auth.setUser(null);
        localStorage.removeItem('permissions');

        return null;
      } finally {
        setLoading(false);
        inflightRef.current = null;
      }
    })();

    inflightRef.current = task;

    return task;
  }, [auth, location.pathname]);

  useEffect(() => {
    // Don’t hydrate on public auth pages; it causes pointless /me calls and can
    // create loops after logout or on hard reload.
    if (isPublicPath(location.pathname)) {
      setLoading(false);

      return;
    }

    // If we already have a user in store (e.g. navigated within SPA), don’t refetch.
    if (auth.user?.id) {
      setLoading(false);

      return;
    }

    // If we already tried and failed (unauth), don’t keep retrying on every mount.
    if (hadAuthErrorRef.current) {
      setLoading(false);

      return;
    }

    fetchMe().then();
  }, [auth.user?.id, fetchMe, location.pathname]);

  return { me, refreshMe: fetchMe, loading };
};
